import { ListItem } from "@mui/material";
import { SxProps } from "@mui/system";
import { useCallback, useMemo } from "react";
import { NameIdOption } from "../../../../../common/types/commonTypes";
import { ExpertUser } from "../../../../../features/user/types/entities";
import AutocompleteBase from "../autocompleteBase/AutocompleteBase";
import { handleAutocompleteChange } from "../helpers";
import { normalizeSelectedExpertValues } from "./expertsAutocompleteUtils";
import useExpertUsersAutocomplete from "./useExpertUsersAutocomplete";

interface Props {
  value?: NameIdOption[];
  onChange?: (experts: NameIdOption[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: "small" | "medium";
  limitTags?: number;
  includeNoHrRep?: boolean;
  includeExpert?: (expert: ExpertUser) => boolean;
  placeholder?: string;
  sx?: SxProps;
}

const ExpertsAutocomplete = ({
  value,
  onChange,
  multiple = true,
  disabled = false,
  fullWidth = true,
  size = "small",
  limitTags,
  includeNoHrRep = false,
  includeExpert,
  placeholder,
  sx = {},
}: Props) => {
  const { expertOptions, isLoading } =
    useExpertUsersAutocomplete(includeNoHrRep, includeExpert);

  const normalizedValue = useMemo(
    () => normalizeSelectedExpertValues(value, expertOptions),
    [value, expertOptions],
  );

  const handleChange = useCallback(
    (newValue: NameIdOption | NameIdOption[] | null) => {
      handleAutocompleteChange(newValue, onChange, multiple);
    },
    [onChange, multiple],
  );

  const getOptionLabel = (option: NameIdOption) => option.name;
  const isOptionEqualToValue = (option: NameIdOption, val: NameIdOption) =>
    option.id === val.id;

  return (
    <AutocompleteBase<NameIdOption>
      value={normalizedValue}
      onChange={(newValue) => handleChange(newValue)}
      options={expertOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      limitTags={limitTags}
      size={size}
      loading={isLoading}
      placeholder={placeholder}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          {option.name}
        </ListItem>
      )}
      sx={sx}
    />
  );
};

export default ExpertsAutocomplete;
